import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../../shared/components/buttons/custom-button";
import { TCComponentRawMaterialInformation } from "../../../../tc-models/tc-req-dto/tc-component-details.req_dto";
import { useFormActions } from "../../../../../../../shared/hooks/form-builder.hooks";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { useTransactionCertificateStore } from "../../../../../../../store/transaction-certificates.store";
import useNotification from "../../../../../../../shared/hooks/useNotification";
import { Utills } from "../../../../../../../shared/app-configs/utilit";


interface RawMaterialsInfoProps {
  rawMaterialInfoData: TCComponentRawMaterialInformation[]; // Expecting an array
  setRawMaterialInfoData: React.Dispatch<
    React.SetStateAction<TCComponentRawMaterialInformation[]>
  >;
  onValidationChange?: (isValid: boolean) => void; // Callback to notify parent about validation status
  componentNo?: string; // Add componentNo prop
  productRawMaterialCodes?: string[]; // Raw material codes from associated product
  certifiedRawMaterialCodes?: Array<{ standard: string; material_code: string }>;
  tcStandardList?: string[];
}

interface RawMaterialOption {
  label: string;
  value: string;
}


// Define Yup validation schema for each row


const RawMaterialInfo: React.FC<RawMaterialsInfoProps> = ({
  rawMaterialInfoData,
  setRawMaterialInfoData,
  onValidationChange,
  componentNo = "", // Destructure componentNo prop
  productRawMaterialCodes = [], // Destructure productRawMaterialCodes prop
  certifiedRawMaterialCodes = [],
  tcStandardList = [],
}) => {
  const { notify } = useNotification()
  const {isRestrictPopUpSave, setRestrictPopUpSave } = useTransactionCertificateStore();
  const [rawMaterials, setRawMaterials] = useState<RawMaterialOption[]>([]);
  const [rawMaterialsLoading, setRawMaterialsLoading] = useState<boolean>(false);
  const [data, setData] = useState<TCComponentRawMaterialInformation[]>(
    rawMaterialInfoData
  ); // Initialize with prop data
  const [tableData, setTableData] = useState<TCComponentRawMaterialInformation[]>(
    rawMaterialInfoData
  ); // Initialize with prop data
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const { updateField } = useFormActions("component-details");

  // Calculate total percentage from data (excluding empty rows)
  const calculateTotalPercentage = (dataArray: TCComponentRawMaterialInformation[]): number => {
    return dataArray.reduce((sum, item) => {
      // Only count rows that have a code or percentage > 0
      if (item.tcProductComponentRawMaterialCode || (item.tcProductComponentRawMaterialPercentage ?? 0) > 0) {
        return sum + (item.tcProductComponentRawMaterialPercentage || 0);
      }
      return sum;
    }, 0);
  };

  // Check if total is exactly 100%
  const isTotalOneHundred = (): boolean => {
    const total = calculateTotalPercentage(data);
    return total === 100;
  };

  // Check if total exceeds 100%
  const isTotalExceedsHundred = (): boolean => {
    const total = calculateTotalPercentage(data);
    return total > 100;
  };

  // Get current total percentage
  const getTotalPercentage = (): number => {
    return calculateTotalPercentage(data);
  };

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        tcProductComponentRawMaterialCode: Yup.string()
          .when([], {
            is: () => componentNo && componentNo.toLowerCase().trim() !== "na",
            then: (schema) =>
              schema
                .required(
                  "Component Raw Material Code is required when Component No is not 'na'"
                )
                .test(
                  "unique-component-rm-code",
                  "Component Raw Material Code must be unique",
                  function (value) {
                    if (!value || editingRowIndex === -1) return true;
                    const selected = value.trim();
                    const hasDuplicate = data.some((row, index) => {
                      if (index === editingRowIndex) return false;
                      const rowValue = (row.tcProductComponentRawMaterialCode || "").trim();
                      return rowValue !== "" && rowValue === selected;
                    });
                    return !hasDuplicate;
                  }
                ),
            otherwise: (schema) =>
              schema
                .nullable()
                .test(
                  "unique-component-rm-code",
                  "Component Raw Material Code must be unique",
                  function (value) {
                    if (!value || editingRowIndex === -1) return true;
                    const selected = value.trim();
                    const hasDuplicate = data.some((row, index) => {
                      if (index === editingRowIndex) return false;
                      const rowValue = (row.tcProductComponentRawMaterialCode || "").trim();
                      return rowValue !== "" && rowValue === selected;
                    });
                    return !hasDuplicate;
                  }
                ),
          }),
        tcProductComponentRawMaterialPercentage: Yup.number()
          .when([], {
            is: () => componentNo && componentNo.toLowerCase().trim() !== "na",
            then: (schema) =>
              schema
                .required(
                  "Component Raw Material Percentage is required when Component No is not 'na'"
                )
                .typeError("Percentage must be a number")
                .min(0, "Percentage must be positive")
                .max(100, "Percentage cannot exceed 100")
                .test(
                  "percentage-max-5-decimals",
                  "Only 5 decimal places allowed",
                  function (value) {
                    if (value != null) {
                      const decimals = (String(value).split(".")[1] || "").length;
                      return decimals <= 5;
                    }
                    return true;
                  }
                )
                .test(
                  "percentage-total-not-exceed-100",
                  "Total raw material percentage cannot exceed 100%",
                  function (value) {
                    if (editingRowIndex > -1 && value != null) {
                      let total = data.reduce((sum, item, index) => {
                        if (index === editingRowIndex) return sum;
                        // Only count rows that have a code or percentage > 0
                        if (
                          item.tcProductComponentRawMaterialCode ||
                          (item.tcProductComponentRawMaterialPercentage ?? 0) > 0
                        ) {
                          return sum + (item.tcProductComponentRawMaterialPercentage || 0);
                        }
                        return sum;
                      }, 0);
                      return total + value <= 100;
                    }
                    return true;
                  }
                ),
            otherwise: (schema) =>
              schema
                .nullable()
                .typeError("Percentage must be a number")
                .min(0, "Percentage must be positive")
                .max(100, "Percentage cannot exceed 100")
                .test(
                  "percentage-max-5-decimals",
                  "Only 5 decimal places allowed",
                  function (value) {
                    if (value != null) {
                      const decimals = (String(value).split(".")[1] || "").length;
                      return decimals <= 5;
                    }
                    return true;
                  }
                )
                .test(
                  "percentage-total-not-exceed-100",
                  "Total raw material percentage cannot exceed 100%",
                  function (value) {
                    if (editingRowIndex > -1 && value != null) {
                      let total = data.reduce((sum, item, index) => {
                        if (index === editingRowIndex) return sum;
                        // Only count rows that have a code or percentage > 0
                        if (
                          item.tcProductComponentRawMaterialCode ||
                          (item.tcProductComponentRawMaterialPercentage ?? 0) > 0
                        ) {
                          return sum + (item.tcProductComponentRawMaterialPercentage || 0);
                        }
                        return sum;
                      }, 0);
                      return total + value <= 100;
                    }
                    return true;
                  }
                ),
          }),
      }),
    [componentNo, data, editingRowIndex]
  );
  
  useEffect(() => {
    setData(rawMaterialInfoData);
    setTableData(rawMaterialInfoData);
  }, [rawMaterialInfoData]);

  // Notify parent when data changes about validation status
  useEffect(() => {
    if (onValidationChange) {
      // Trigger validation change callback when data changes
      onValidationChange(!isTotalExceedsHundred());
    }
  }, [data, onValidationChange]);

  useEffect(()=>{
    const getRawMaterialCodes = async () => {
      setRawMaterialsLoading(true);
      try {
        const result: any = await dtrackItService.getRawMaterialCodes()

  // Debug: result getProductDetails removed

        const rawMaterials = Array.isArray(result?.data)
          ? result.data.map((t: any) => ({ value: t.code_1, label: `${t.description_1} (${t.code_1})` }))
          : [];
  // Debug: rawMaterials fetched
        setRawMaterials(rawMaterials);
      } catch (err) {
  // Error fetching raw material codes
      } finally {
        setRawMaterialsLoading(false);
      }
    };
    getRawMaterialCodes()
  },[])

  const filteredRawMaterials = React.useMemo(() => {
    if (rawMaterialsLoading) {
      return [{ value: "__loading__", label: "Loading..." }];
    }

    // If product has no raw materials yet, show all raw materials
    if (!productRawMaterialCodes || productRawMaterialCodes.length === 0) {
      return rawMaterials;
    }

    // Filter to only show raw materials that exist in product's raw material codes
    return rawMaterials.filter((rm) => productRawMaterialCodes.includes(rm.value));
  }, [rawMaterials, rawMaterialsLoading, productRawMaterialCodes]);

  const displayedRawMaterials: RawMaterialOption[] = filteredRawMaterials;
  
  // Check if existing component raw materials don't match product's raw material codes
  const hasMismatchedRawMaterials = React.useMemo(() => {
    if (!productRawMaterialCodes || productRawMaterialCodes.length === 0) {
      return false; // No product raw materials yet, so no mismatch
    }
    
    return data.some((item) => {
      if (!item.tcProductComponentRawMaterialCode) return false;
      return !productRawMaterialCodes.includes(item.tcProductComponentRawMaterialCode);
    });
  }, [data, productRawMaterialCodes]);
  const formik = useFormik<TCComponentRawMaterialInformation>({
    initialValues: {
      tcProductComponentRawMaterialPercentage: 0, //  number | null
      tcProductComponentRawMaterialCode: "",
      tcProductComponentRawMaterialCodeDescription: ""
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    const parsedValue =
      columnId === "tcProductComponentRawMaterialPercentage"
        ? value === "" ? null : Number(value) // convert string to number | null
        : value;
  
    formik.setFieldValue(columnId, parsedValue);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: parsedValue } : row
      )
    );
  };  

  const handleAddNewRow = () => {
    const newRow: TCComponentRawMaterialInformation = {
      tcProductComponentRawMaterialPercentage: 0, //  number | null
      tcProductComponentRawMaterialCode: "",
      tcProductComponentRawMaterialCodeDescription: ""
    };
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    setRestrictPopUpSave(true);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    setData(tableData);
    setRestrictPopUpSave(false)
    // Reset editing state and form
    setEditingRowIndex(-1);
    formik.resetForm();
  };
  const handleSaveRow = async () => {
    if (editingRowIndex === null) return;

    // Validate the form before saving
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      const errorMessages = Object.values(errors).filter(Boolean);
      if (errorMessages.length > 0) {
        notify(errorMessages[0] as string, "error");
      }
      return;
    }

    const updatedRow = formik.values;
    const newData = data.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );    
    setData(newData);
    setTableData(newData);
    setRawMaterialInfoData(newData); // Update parent with updated data (not old data)
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false)
    formik.resetForm();
  };

  const columns: EditableColumnDef<TCComponentRawMaterialInformation>[] = [
    {
      accessorKey: "tcProductComponentRawMaterialCode",
      header: "Component Raw Material Code",
      Header: () => {
        const isRequired = componentNo && componentNo.toLowerCase().trim() !== "na";
        return (
          <span>
            {isRequired && <i style={{ color: "red" }}>*</i>} Component Raw Material Code
          </span>
        );
      },
      renderEditCell: ({ value, onChange, rowIndex }) => (
        <CustomSelect
          name="tcProductComponentRawMaterialCode"
          value={value}
          options={displayedRawMaterials}
          isOptionDisabled={(option:any)=> rawMaterialsLoading || option?.value === "__loading__"}
          enableSearch={true}
          placeholder="Select Raw Material Code"
          onChange={(e) => {
            const selectedCode = e.target.value;
            const selectedMaterial = rawMaterials.find(
              (mat) => mat.value === selectedCode
            );
    
            const description = selectedMaterial
              ? selectedMaterial.label.split(" (")[0]
              : "";
    
            //  Update both fields
            onChange(selectedCode);
            formik.setFieldValue("tcProductComponentRawMaterialCodeDescription", description);
            handleCellChange(rowIndex, "tcProductComponentRawMaterialCodeDescription", description);
          }}
        />
      ),
    },
    {
      accessorKey: "tcProductComponentRawMaterialPercentage",
      header: "Component Raw Material Percentage",
      Header: () => {
        const isRequired = componentNo && componentNo.toLowerCase().trim() !== "na";
        return (
          <span>
            {isRequired && <i style={{ color: "red" }}>*</i>} Component Raw Material Percentage
          </span>
        );
      },
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="tcProductComponentRawMaterialPercentage"
          value={value ?? ""} // handle null safely
          inputProps={{ max: 100, step: "0.00001" }}
          type="number"
          onChange={(e) => {
            const newValue = e.target.value;
            const matched = String(newValue).match(/^\d*(?:\.(\d{0,5})?)?$/);
            const num = Number(newValue);
            if (matched && !Number.isNaN(num) && num >= 0 && num <= 100) {
              onChange(newValue);
            }
          }}
          placeholder="Enter value (max 5 decimals)"
          sx={{ mb: 2 }}
        />
      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setRawMaterialInfoData(updatedData); // Update parent with updated data
  };

  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        {hasMismatchedRawMaterials && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              bgcolor: "warning.light",
              color: "warning.contrastText",
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              Warning: Some component raw material codes do not match the selected product's raw material codes.
              Please update them to match the product's raw materials.
            </Typography>
          </Box>
        )}
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton 
            disabled={isRestrictPopUpSave || isTotalOneHundred()} 
            onClick={handleAddNewRow} 
            sx={{ mb: 2 }}
          >
            Add Raw Material Info
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
        />
      </Box>
    </div>
  );
};

export default RawMaterialInfo;
